/**
 * RewardsCumulativeChart Component
 *
 * Displays cumulative rewards evolution over time as an area chart.
 * Isolated in its own component with React.memo to prevent re-render issues.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import type { RewardPayout } from '../../services/fluxNodeApi';

interface RewardsCumulativeChartProps {
  rewardPayouts: RewardPayout[];
}

// Helper to safely format numbers
const safeNum = (value: unknown): number => {
  if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
    return value;
  }
  return 0;
};

function RewardsCumulativeChartInner({ rewardPayouts }: RewardsCumulativeChartProps) {
  const { t } = useTranslation();

  // Calculate cumulative rewards data
  const chartData = useMemo(() => {
    if (!rewardPayouts || rewardPayouts.length === 0) return [];

    // Sort payouts by timestamp ascending (oldest first)
    const sortedPayouts = [...rewardPayouts].sort((a, b) => a.timestamp - b.timestamp);

    // Build cumulative data
    let runningTotal = 0;
    const cumulativeData: Array<{
      date: string;
      timestamp: number;
      daily: number;
      cumulative: number;
    }> = [];

    // Group by day and calculate cumulative
    const dailyMap = new Map<string, { timestamp: number; daily: number }>();

    for (const payout of sortedPayouts) {
      const date = new Date(payout.timestamp);
      const dateKey = date.toISOString().split('T')[0];

      if (!dailyMap.has(dateKey)) {
        dailyMap.set(dateKey, { timestamp: payout.timestamp, daily: 0 });
      }
      const entry = dailyMap.get(dateKey)!;
      entry.daily += safeNum(payout.amount);
    }

    // Convert to cumulative array
    const sortedDays = Array.from(dailyMap.entries()).sort((a, b) => a[1].timestamp - b[1].timestamp);

    for (const [dateKey, data] of sortedDays) {
      runningTotal += data.daily;
      const date = new Date(data.timestamp);
      cumulativeData.push({
        date: date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
        timestamp: data.timestamp,
        daily: Number(data.daily.toFixed(4)),
        cumulative: Number(runningTotal.toFixed(4))
      });
    }

    // Limit to last 90 days for readability
    const ninetyDaysAgo = Date.now() - 90 * 24 * 60 * 60 * 1000;
    return cumulativeData.filter(d => d.timestamp >= ninetyDaysAgo);
  }, [rewardPayouts]);

  if (chartData.length === 0) {
    return (
      <div style={{
        height: '220px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--muted)'
      }}>
        {t('fluxNode.rewards.noData', 'No reward data available')}
      </div>
    );
  }

  return (
    <div style={{ height: '220px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorCumulative" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: 'var(--muted)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'var(--muted)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
              tickFormatter={(value) => `${value}`}
              width={50}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--card-bg)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                fontSize: '12px'
              }}
              formatter={(value: number, name: string) => [
                `${safeNum(value).toFixed(4)} FLUX`,
                name === 'cumulative' ? t('fluxNode.rewards.cumulative', 'Cumulative') : t('fluxNode.rewards.daily', 'Daily')
              ]}
              labelFormatter={(label) => `${t('fluxNode.rewards.date', 'Date')}: ${label}`}
            />
            <Area
              type="monotone"
              dataKey="cumulative"
              stroke="#10b981"
              strokeWidth={2}
              fill="url(#colorCumulative)"
              dot={false}
              activeDot={{ r: 4, fill: '#10b981' }}
            />
          </AreaChart>
        </ResponsiveContainer>
    </div>
  );
}

// Export with React.memo to prevent unnecessary re-renders
export const RewardsCumulativeChart = React.memo(RewardsCumulativeChartInner);
